<template>
  <div class="soc-smart-guide-panel">
    <div v-if="!sessionActive" class="guide-start">
      <p class="guide-lead">按角色选流程，或说目标让系统解析；代跑店员线须填门店 ID。</p>
      <p class="guide-honesty muted">
        {{ guideHonestyZh || '固定步驟 Smart Guide（playbook），≠ Brand Agent 對話發現式建品牌／店' }}
        <el-tag v-if="guideClass" size="mini" type="info" effect="plain" style="margin-left:6px">{{ guideClass }}</el-tag>
      </p>
      <div class="guide-role-row">
        <span class="guide-role-label">角色预览</span>
        <el-radio-group v-model="previewRole" size="mini" @change="loadPlaybooks">
          <el-radio-button label="">当前账号</el-radio-button>
          <el-radio-button label="store_manager">店长</el-radio-button>
          <el-radio-button label="store_clerk">店员</el-radio-button>
          <el-radio-button label="brand_ops">品牌</el-radio-button>
          <el-radio-button label="platform_admin">超管</el-radio-button>
        </el-radio-group>
      </div>
      <div class="guide-intent-row">
        <el-input
          v-model="intentText"
          size="mini"
          placeholder="例如：我是店长，要把饮料机从开店带到能出杯"
          @keyup.enter.native="resolveIntent"
        />
        <el-button size="mini" :loading="loading" @click="resolveIntent">解析意图</el-button>
      </div>
      <div v-if="clarifyQuestions.length" class="guide-clarify">
        <div v-for="q in clarifyQuestions" :key="q.id" class="guide-clarify-q">
          <p>{{ q.promptZh }}</p>
          <el-button
            v-for="opt in (q.options || [])"
            :key="q.id + '-' + opt.value"
            size="mini"
            @click="answerClarify(q.id, opt.value)"
          >{{ opt.labelZh || opt.value }}</el-button>
        </div>
      </div>
      <div v-if="proxyStoreNeeded" class="guide-store-row">
        <el-input v-model="proxyStoreId" size="mini" placeholder="代跑店员线：门店 ID" />
      </div>
      <div class="guide-start-actions">
        <el-button
          v-for="pb in activePlaybooks"
          :key="pb.playbookId"
          :type="pb.accessLevel === 'primary' ? 'primary' : 'default'"
          size="small"
          :loading="loading"
          @click="startGuide(pb.playbookId)"
        >
          {{ accessMark(pb.accessLevel) }}{{ pb.titleZh || pb.playbookId }}
        </el-button>
        <el-button
          v-if="!activePlaybooks.length"
          type="primary"
          size="small"
          :loading="loading"
          @click="startGuide('store_manager_dispenser_r1')"
        >
          开始：店长 · 饮料机
        </el-button>
      </div>
    </div>
    <div v-else class="guide-active">
      <div class="guide-head">
        <strong>{{ titleZh }}</strong>
        <el-tag size="mini" type="success">智能引导</el-tag>
      </div>
      <ol class="guide-steps">
        <li
          v-for="s in steps"
          :key="s.stepId"
          :class="{ current: s.status === 'CURRENT', done: s.status === 'DONE' }"
        >
          <span class="step-mark">{{ stepMark(s) }}</span>
          <span>{{ s.titleZh }}</span>
        </li>
      </ol>
      <div v-if="currentStep" class="guide-current">
        <p class="guide-instruction">{{ currentStep.instructionZh }}</p>
        <p
          v-if="boundMachineCode && (currentStep.stepId === 'verify_one_produce' || currentStep.stepId === 'clerk_verify_produce')"
          class="guide-sn"
        >
          验收 SN：{{ boundMachineCode }}（点「去操作此页」会自动带上）
        </p>
        <p v-if="verifyReasonZh" class="guide-verify" :class="{ ok: verifyPassed, bad: !verifyPassed }">
          {{ verifyReasonZh }}
        </p>
        <div class="guide-actions">
          <el-button type="primary" size="mini" @click="goCurrentPage">去操作此页</el-button>
          <el-button
            v-if="currentStep && currentStep.appAction === 'sim_xt90_brew'"
            type="success"
            size="mini"
            :loading="loading"
            @click="assistSimBrew"
          >模拟点饮验收</el-button>
          <el-button size="mini" :loading="loading" @click="refresh">检查是否完成</el-button>
          <el-button size="mini" type="text" @click="cancelGuide">结束引导</el-button>
        </div>
      </div>
      <p v-if="completedBanner" class="guide-done">已完成引导闭环，可关闭。</p>
    </div>
  </div>
</template>

<script>
import {
  fetchSmartGuideHonestyStatus,
  fetchSmartGuidePlaybooks,
  resolveSmartGuideIntent,
  startSmartGuideSession,
  fetchActiveSmartGuideSession,
  refreshSmartGuideSession,
  cancelSmartGuideSession,
  assistSmartGuideSession
} from '@/api/socSmartGuide'
import { leoOperatorAssistantBus } from '@/utils/leoOperatorAssistant'

export default {
  name: 'SocSmartGuidePanel',
  data () {
    return {
      loading: false,
      sessionId: null,
      titleZh: '',
      steps: [],
      currentStep: null,
      verifyPassed: false,
      verifyReasonZh: '',
      completedBanner: false,
      pollTimer: null,
      boundMachineCode: '',
      activePlaybooks: [],
      previewRole: '',
      intentText: '',
      clarifyQuestions: [],
      clarifyAnswers: {},
      pendingEntryStepId: null,
      pendingPathHint: null,
      proxyStoreId: '',
      proxyStoreNeeded: false,
      guideHonestyZh: '',
      guideClass: ''
    }
  },
  computed: {
    sessionActive () {
      return !!this.sessionId
    }
  },
  mounted () {
    this.loadHonesty()
    this.loadPlaybooks()
    this.loadActive()
    leoOperatorAssistantBus.$on('smart-guide-changed', this.refreshQuiet)
  },
  beforeDestroy () {
    this.stopPoll()
    leoOperatorAssistantBus.$off('smart-guide-changed', this.refreshQuiet)
  },
  methods: {
    accessMark (level) {
      if (level === 'primary') return '● '
      if (level === 'proxy') return '○ '
      if (level === 'limited') return '△ '
      return ''
    },
    stepMark (s) {
      if (s.status === 'DONE') return '✓'
      if (s.status === 'CURRENT') return '→'
      return '·'
    },
    async loadHonesty () {
      try {
        const res = await fetchSmartGuideHonestyStatus()
        const d = (res && res.data) || res || {}
        this.guideHonestyZh = d.honestyZh || ''
        this.guideClass = d.guideClass || ''
      } catch (e) {
        this.guideHonestyZh = '固定步驟 Smart Guide（playbook），≠ Brand Agent 對話發現式建品牌／店'
        this.guideClass = 'FIXED_PLAYBOOK'
      }
    },
    async loadPlaybooks () {
      try {
        const res = await fetchSmartGuidePlaybooks(this.previewRole || undefined)
        const list = (res && res.data) || []
        this.activePlaybooks = list.filter(function (p) {
          return p && String(p.status || '').toLowerCase() === 'active'
        })
        const first = this.activePlaybooks[0]
        if (first && first.honestyZh && !this.guideHonestyZh) {
          this.guideHonestyZh = first.honestyZh
          this.guideClass = first.guideClass || 'FIXED_PLAYBOOK'
        }
      } catch (e) {
        this.activePlaybooks = [
          { playbookId: 'store_manager_dispenser_r1', titleZh: '店长 · 饮料机', status: 'active', accessLevel: 'primary' },
          { playbookId: 'store_clerk_dispenser_r2', titleZh: '店员 · 饮料机', status: 'active', accessLevel: 'proxy' },
          { playbookId: 'store_manager_tea_r2', titleZh: '店长 · 茶机', status: 'active', accessLevel: 'primary' },
          { playbookId: 'store_manager_coffee_r2', titleZh: '店长 · 咖啡机', status: 'active', accessLevel: 'primary' },
          { playbookId: 'brand_ops_org_device_r2', titleZh: '品牌 · 组织设备', status: 'active', accessLevel: 'proxy' },
          { playbookId: 'ops_inventory_r2', titleZh: '营运 · 库存闭环', status: 'active', accessLevel: 'primary' },
          { playbookId: 'platform_admin_map_r2', titleZh: '超管 · 全图代开', status: 'active', accessLevel: 'proxy' }
        ]
      }
    },
    async resolveIntent () {
      this.loading = true
      try {
        const payload = Object.assign({ utterance: this.intentText || '' }, this.clarifyAnswers)
        if (this.previewRole) payload.guideRole = this.previewRole
        const res = await resolveSmartGuideIntent(payload)
        const data = res && res.data
        this.clarifyQuestions = (data && data.clarify) || []
        if (data && data.resolved && data.playbookId) {
          this.pendingEntryStepId = data.entryStepId || null
          this.pendingPathHint = data.pathHint || this.clarifyAnswers.catalogPath || this.clarifyAnswers.pathHint || null
          this.proxyStoreNeeded = String(data.playbookId).indexOf('store_clerk_') === 0
          await this.startGuide(data.playbookId)
        } else if (!this.clarifyQuestions.length) {
          this.$message.warning('无法解析意图，请点选一条引导')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '意图解析失败')
      } finally {
        this.loading = false
      }
    },
    answerClarify (id, value) {
      this.$set(this.clarifyAnswers, id, value)
      if (id === 'role') this.clarifyAnswers.guideRole = value
      if (id === 'deviceFamily') this.clarifyAnswers.deviceFamily = value
      if (id === 'goal') this.clarifyAnswers.goal = value
      if (id === 'catalogPath' || id === 'pathHint') {
        this.clarifyAnswers.pathHint = value
        this.clarifyAnswers.catalogPath = value
        this.pendingPathHint = value
      }
      this.resolveIntent()
    },
    applySession (data) {
      if (!data || !data.active) {
        this.sessionId = null
        this.steps = []
        this.currentStep = null
        this.completedBanner = false
        this.stopPoll()
        return
      }
      this.sessionId = data.sessionId
      this.titleZh = data.titleZh || ''
      this.steps = data.steps || []
      this.currentStep = data.currentStep || null
      this.boundMachineCode = data.boundMachineCode
        || (data.context && data.context.imie)
        || ''
      if (this.currentStep) {
        this.verifyPassed = !!this.currentStep.verifyPassed
        this.verifyReasonZh = this.currentStep.verifyReasonZh || ''
      }
      this.completedBanner = data.status === 'COMPLETED' || !!data.completedNow
      if (data.active && !this.completedBanner) {
        this.startPoll()
      } else {
        this.stopPoll()
      }
    },
    async loadActive () {
      try {
        const res = await fetchActiveSmartGuideSession()
        this.applySession(res && res.data)
      } catch (e) {
        // ignore
      }
    },
    async startGuide (playbookId) {
      const id = playbookId || 'store_manager_dispenser_r1'
      if (String(id).indexOf('store_clerk_') === 0) {
        this.proxyStoreNeeded = true
      }
      const opts = {}
      if (this.pendingEntryStepId) opts.entryStepId = this.pendingEntryStepId
      if (this.proxyStoreId) opts.storeId = this.proxyStoreId
      if (this.pendingPathHint) opts.pathHint = this.pendingPathHint
      else if (this.clarifyAnswers.pathHint) opts.pathHint = this.clarifyAnswers.pathHint
      else if (String(id) === 'store_manager_dispenser_r1') opts.pathHint = 'auto'
      this.loading = true
      try {
        const res = await startSmartGuideSession(id, opts)
        this.pendingEntryStepId = null
        this.pendingPathHint = null
        this.clarifyQuestions = []
        this.applySession(res && res.data)
        this.goCurrentPage()
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        this.$message.success('智能引导已开始')
      } catch (e) {
        const msg = (e && e.message) || '无法开始引导'
        if (String(msg).indexOf('STORE_REQUIRED') >= 0) {
          this.proxyStoreNeeded = true
          this.$message.warning('代跑店员线请先填门店 ID')
        } else {
          this.$message.error(msg)
        }
      } finally {
        this.loading = false
      }
    },
    goCurrentPage () {
      const routePath = this.currentStep && this.currentStep.routePath
      if (!routePath) return
      const query = {}
      if (routePath.indexOf('simulator-xt90') >= 0 && this.boundMachineCode) {
        query.machineCode = this.boundMachineCode
      }
      if (this.$route.path !== routePath || (query.machineCode && this.$route.query.machineCode !== query.machineCode)) {
        this.$router.push({ path: routePath, query: query })
      }
    },
    async assistSimBrew () {
      if (!this.sessionId) return
      this.loading = true
      try {
        const res = await assistSmartGuideSession(this.sessionId, 'sim_xt90_brew')
        this.applySession(res && res.data)
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        this.$message.success('已触发模拟点饮')
      } catch (e) {
        this.$message.error((e && e.message) || '模拟点饮失败')
      } finally {
        this.loading = false
      }
    },
    async refresh () {
      if (!this.sessionId) return
      this.loading = true
      try {
        const res = await refreshSmartGuideSession(this.sessionId)
        const data = res && res.data
        if (data && data.verifyReasonZh) {
          this.verifyPassed = !!data.verifyPassed
          this.verifyReasonZh = data.verifyReasonZh
        }
        this.applySession(data)
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        if (data && data.advanced) {
          this.$message.success('本步已完成，进入下一步')
          this.goCurrentPage()
        } else if (data && data.completedNow) {
          this.$message.success('引导全部完成')
        } else if (data && data.verifyPassed === false) {
          this.$message.warning(data.verifyReasonZh || '尚未完成')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '检查失败')
      } finally {
        this.loading = false
      }
    },
    async cancelGuide () {
      if (!this.sessionId) return
      try {
        await cancelSmartGuideSession(this.sessionId)
      } catch (e) {
        // still clear local
      }
      this.applySession(null)
      leoOperatorAssistantBus.$emit('smart-guide-changed')
      this.$message.info('已结束引导')
    },
    startPoll () {
      this.stopPoll()
      const self = this
      this.pollTimer = setInterval(function () {
        if (self.sessionId && !self.loading) {
          self.refreshQuiet()
        }
      }, 8000)
    },
    stopPoll () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async refreshQuiet () {
      if (!this.sessionId) return
      try {
        const res = await refreshSmartGuideSession(this.sessionId)
        const data = res && res.data
        const prevIdx = this.currentStep && this.currentStep.index
        this.applySession(data)
        if (data && data.advanced) {
          this.$message.success('本步已完成，进入下一步')
          this.goCurrentPage()
        } else if (data && data.completedNow) {
          this.$message.success('引导全部完成')
        } else if (data && data.currentStep && data.currentStep.index !== prevIdx) {
          this.goCurrentPage()
        }
      } catch (e) {
        // quiet
      }
    }
  }
}
</script>

<style scoped>
.soc-smart-guide-panel {
  margin-bottom: 14px;
  padding: 12px;
  background: #f5f9ff;
  border-radius: 8px;
  border: 1px solid #d6e4ff;
}
.guide-lead {
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.5;
}
.guide-honesty {
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #909399;
}
.guide-role-row,
.guide-intent-row,
.guide-store-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.guide-role-label {
  font-size: 12px;
  color: #606266;
}
.guide-intent-row .el-input {
  flex: 1;
  min-width: 180px;
}
.guide-clarify {
  margin-bottom: 10px;
  padding: 8px;
  background: #fff;
  border-radius: 6px;
  border: 1px dashed #c0d4ff;
}
.guide-clarify-q p {
  margin: 0 0 6px;
  font-size: 12px;
}
.guide-clarify-q .el-button {
  margin: 0 6px 6px 0;
}
.guide-start-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.guide-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
}
.guide-steps {
  margin: 0 0 10px;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.7;
  color: #606266;
}
.guide-steps .current {
  color: #409eff;
  font-weight: 600;
}
.guide-steps .done {
  color: #67c23a;
}
.step-mark {
  display: inline-block;
  width: 14px;
}
.guide-instruction {
  margin: 0 0 6px;
  font-size: 13px;
  line-height: 1.5;
}
.guide-sn {
  margin: 0 0 6px;
  font-size: 12px;
  color: #909399;
}
.guide-verify {
  margin: 0 0 8px;
  font-size: 12px;
}
.guide-verify.ok {
  color: #67c23a;
}
.guide-verify.bad {
  color: #e6a23c;
}
.guide-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.guide-done {
  margin: 10px 0 0;
  color: #67c23a;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/LeoKnowledge/SocSmartGuidePanel.vue